import React, { useContext, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { UserContext } from "../../context/UserProvider";
import useApiEp from "../../hooks/useApiEp";
import { getErrorResponse } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import MuPb from "../../widgets/MuPb";

const numberFormatter = new Intl.NumberFormat("en-IN");
const compactFormatter = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const MONTHLY_INCLUDED_MESSAGES = 1000;

const formatNumber = (value) => numberFormatter.format(value ?? 0);
const formatCompact = (value) => compactFormatter.format(value ?? 0);
const percentage = (value, total) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

const formatMonth = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });

const formatDay = (value) => {
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
};

const formatDate = (value) => {
  if (!value) return "No recent activity";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No recent activity";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatCard = ({ label, value, helper, icon, color, featured }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      borderRadius: 3.5,
      borderColor: `${color}30`,
      background: featured
        ? `linear-gradient(135deg, ${color}18 0%, #ffffff 75%)`
        : "#fff",
      boxShadow: featured ? `0 16px 40px ${color}14` : "none",
    }}
  >
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography
            variant={featured ? "h3" : "h4"}
            fontWeight={850}
            sx={{ color, letterSpacing: "-0.045em", my: 0.7 }}
          >
            {formatCompact(value)}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            bgcolor: `${color}12`,
            color,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const InsightCard = ({ title, value, description, icon, color }) => (
  <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: "100%" }}>
    <Box display="flex" gap={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: `${color}12`,
          color,
          borderRadius: 2.5,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={850} sx={{ color, my: 0.25 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const MyConversationReport = () => {
  const { user } = useContext(UserContext);
  const { data, isLoading, isFetching, isError, error, refetch } = useApiEp(
    "/user/get-my-conversation-report"
  );
  const report = data?.data;
  const summary = report?.summary ?? {};

  const monthlyOutgoing = useMemo(
    () =>
      (report?.monthlyOutgoing ?? []).map((item) => ({
        ...item,
        label: formatMonth(item.year, item.month),
      })),
    [report]
  );

  const dailyActivity = useMemo(
    () =>
      (report?.dailyActivity ?? []).map((item) => ({
        ...item,
        label: formatDay(item.date),
      })),
    [report]
  );

  const currentMonthTotals = useMemo(
    () =>
      (report?.dailyActivity ?? []).reduce(
        (totals, day) => ({
          incoming: totals.incoming + (day.incomingMessages ?? 0),
          outgoing: totals.outgoing + (day.outgoingMessages ?? 0),
        }),
        { incoming: 0, outgoing: 0 }
      ),
    [report]
  );

  const currentMonth = monthlyOutgoing.at(-1)?.outgoingMessages ?? 0;
  const previousMonth = monthlyOutgoing.at(-2)?.outgoingMessages ?? 0;
  const monthChange =
    previousMonth > 0
      ? Math.round(((currentMonth - previousMonth) / previousMonth) * 100)
      : null;
  const twoWayRate = percentage(summary.twoWayContacts, summary.uniqueEngaged);
  const includedMessagesRemaining = Math.max(
    MONTHLY_INCLUDED_MESSAGES - currentMonthTotals.outgoing,
    0
  );
  const messagesAboveIncluded = Math.max(
    currentMonthTotals.outgoing - MONTHLY_INCLUDED_MESSAGES,
    0
  );
  const includedUsagePercent = Math.min(
    percentage(currentMonthTotals.outgoing, MONTHLY_INCLUDED_MESSAGES),
    100
  );
  const firstName = user?.firstName?.trim();

  if (isLoading) return <MuPb />;

  return (
    <Container maxWidth="xl" sx={{ py: 3.5 }}>
      <BackBtn />
      <Box
        display="flex"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        mt={2}
        mb={3}
      >
        <Box>
          <Typography variant="overline" color="success.main" fontWeight={850}>
            Your WhatsApp conversations
          </Typography>
          <Typography variant="h4" fontWeight={850} letterSpacing="-0.035em">
            {firstName ? `${firstName}, here’s your` : "Your"} conversation
            report
          </Typography>
          <Typography color="text.secondary" mt={0.6}>
            Understand who you reached, who replied, and how your chat activity
            is changing over time.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={
            isFetching ? <CircularProgress size={17} /> : <RefreshRoundedIcon />
          }
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh report
        </Button>
      </Box>

      {isError ? (
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      ) : report ? (
        <Box display="flex" flexDirection="column" gap={2.5}>
          <Paper
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 4,
              color: "#fff",
              background:
                "linear-gradient(125deg, #064e3b 0%, #047857 52%, #0f766e 100%)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 260,
                height: 260,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.07)",
                right: -70,
                top: -110,
              }}
            />
            <Box position="relative" maxWidth={760}>
              <Chip
                icon={<InsightsRoundedIcon />}
                label="Conversation pulse"
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  "& .MuiChip-icon": { color: "#fff" },
                }}
              />
              <Typography variant="h5" fontWeight={800} mt={2}>
                {currentMonthTotals.incoming > 0
                  ? `You received ${formatNumber(
                      currentMonthTotals.incoming
                    )} messages this calendar month.`
                  : "Your next customer conversation will appear here."}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", mt: 0.8 }}>
                {twoWayRate > 0
                  ? `${twoWayRate}% of the people you engaged have participated in a two-way conversation.`
                  : "Start direct, helpful conversations to encourage more replies."}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                <Chip
                  label={`${formatNumber(
                    currentMonthTotals.outgoing
                  )} sent this month`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}
                />
                <Chip
                  label={`Last active ${formatDate(summary.lastActivity)}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}
                />
              </Box>
            </Box>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "1.25fr repeat(3, 1fr)",
              },
              gap: 1.5,
            }}
          >
            <StatCard
              label="Messages received"
              value={summary.incomingMessages}
              helper="Replies and enquiries sent to you"
              icon={<ArrowDownwardRoundedIcon />}
              color="#16a34a"
              featured
            />
            <StatCard
              label="Messages sent"
              value={summary.outgoingMessages}
              helper="Your non-template conversation messages"
              icon={<ArrowUpwardRoundedIcon />}
              color="#2563eb"
            />
            <StatCard
              label="People who enquired"
              value={summary.uniqueEnquirers}
              helper="Unique numbers that messaged you"
              icon={<ForumRoundedIcon />}
              color="#7c3aed"
            />
            <StatCard
              label="People you engaged"
              value={summary.uniqueEngaged}
              helper="Unique numbers you messaged"
              icon={<GroupsRoundedIcon />}
              color="#ea580c"
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.25, md: 2.75 },
              borderRadius: 3.5,
              borderColor:
                messagesAboveIncluded > 0 ? "warning.light" : "primary.light",
              background:
                messagesAboveIncluded > 0
                  ? "linear-gradient(135deg, #fff7ed 0%, #ffffff 72%)"
                  : "linear-gradient(135deg, #eff6ff 0%, #ffffff 72%)",
            }}
          >
            <Box
              display="flex"
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={1.5}
            >
              <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6" fontWeight={850}>
                    Monthly service-message tracker
                  </Typography>
                  <Chip label="Estimated" size="small" variant="outlined" />
                </Box>
                <Typography variant="body2" color="text.secondary" mt={0.4}>
                  Outgoing non-template messages recorded during the current IST
                  calendar month.
                </Typography>
              </Box>
              <Box textAlign={{ xs: "left", md: "right" }}>
                <Typography variant="h5" fontWeight={850}>
                  {formatNumber(currentMonthTotals.outgoing)} /{" "}
                  {formatNumber(MONTHLY_INCLUDED_MESSAGES)}
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    messagesAboveIncluded > 0 ? "warning.dark" : "primary.main"
                  }
                  fontWeight={700}
                >
                  {messagesAboveIncluded > 0
                    ? `${formatNumber(
                        messagesAboveIncluded
                      )} messages above the included amount`
                    : `${formatNumber(
                        includedMessagesRemaining
                      )} included messages remaining`}
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={includedUsagePercent}
              color={messagesAboveIncluded > 0 ? "warning" : "primary"}
              sx={{ height: 10, borderRadius: 6, mt: 2 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={1}
            >
              This is an activity estimate from stored conversation messages and
              may not match Meta’s final billable-message classification.
            </Typography>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 1.5,
            }}
          >
            <InsightCard
              title="Two-way contacts"
              value={formatNumber(summary.twoWayContacts)}
              description="People who appear in both your sent and received conversations."
              icon={<AutorenewRoundedIcon />}
              color="#0f766e"
            />
            <InsightCard
              title="Inbound-only enquirers"
              value={formatNumber(summary.inboundOnlyEnquirers)}
              description="People who initiated a conversation outside your outgoing reach."
              icon={<PersonAddAltRoundedIcon />}
              color="#7c3aed"
            />
            <InsightCard
              title="Latest active month"
              value={formatNumber(currentMonth)}
              description={
                monthChange === null
                  ? "Outgoing messages in your latest month with activity."
                  : `${
                      monthChange >= 0 ? "+" : ""
                    }${monthChange}% versus the previous active month.`
              }
              icon={<CampaignRoundedIcon />}
              color="#2563eb"
            />
          </Box>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3.5 }}>
            <Box
              display="flex"
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={1}
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Your monthly outgoing volume
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  See the months when your one-to-one messaging was most active.
                </Typography>
              </Box>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${monthlyOutgoing.length} active month${
                  monthlyOutgoing.length === 1 ? "" : "s"
                }`}
              />
            </Box>
            {monthlyOutgoing.length > 0 ? (
              <Box height={340}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthlyOutgoing}
                    margin={{ top: 8, right: 12, left: 0, bottom: 18 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      minTickGap={30}
                      angle={monthlyOutgoing.length > 12 ? -30 : 0}
                      textAnchor={
                        monthlyOutgoing.length > 12 ? "end" : "middle"
                      }
                      height={monthlyOutgoing.length > 12 ? 60 : 30}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      tickFormatter={formatCompact}
                    />
                    <ChartTooltip
                      formatter={(value) => [
                        formatNumber(value),
                        "Messages sent",
                      ]}
                    />
                    <Bar
                      dataKey="outgoingMessages"
                      name="Messages sent"
                      fill="#2563eb"
                      radius={[7, 7, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box height={220} display="grid" sx={{ placeItems: "center" }}>
                <Typography color="text.secondary">
                  No dated outgoing conversations yet
                </Typography>
              </Box>
            )}
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" },
              gap: 2,
            }}
          >
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3.5 }}>
              <Typography variant="h6" fontWeight={800}>
                Current calendar month
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Your daily sent and received conversation activity.
              </Typography>
              {dailyActivity.length > 0 ? (
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyActivity}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        minTickGap={22}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <ChartTooltip />
                      <Legend />
                      <Bar
                        dataKey="incomingMessages"
                        name="Received"
                        fill="#16a34a"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="outgoingMessages"
                        name="Sent"
                        fill="#2563eb"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box height={300} display="grid" sx={{ placeItems: "center" }}>
                  <Typography color="text.secondary">
                    No conversation activity in the current month
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3.5 }}>
              <Typography variant="h6" fontWeight={800}>
                How people message you
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Formats used across your non-template conversations.
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.25}>
                {(report.messageTypes ?? []).map((item) => {
                  const share = percentage(
                    item.total,
                    (summary.incomingMessages ?? 0) +
                      (summary.outgoingMessages ?? 0)
                  );
                  return (
                    <Box key={item.type}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        mb={0.5}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          textTransform="capitalize"
                        >
                          {item.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(item.total)} · {share}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(share, 100)}
                        sx={{ height: 7, borderRadius: 4 }}
                      />
                    </Box>
                  );
                })}
                {(report.messageTypes ?? []).length === 0 && (
                  <Typography color="text.secondary">
                    No message formats available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 3.5, overflow: "hidden" }}
          >
            <Box sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={800}>
                Your most active conversations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The contacts with the highest two-way message activity.
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Total activity</TableCell>
                    <TableCell>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report.topContacts ?? []).map((contact) => (
                    <TableRow key={contact.phoneNumber} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={750}>
                          {contact.profileName === "undefined"
                            ? "~"
                            : contact.profileName || "WhatsApp contact"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contact.phoneNumber}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "success.main", fontWeight: 750 }}
                      >
                        {formatNumber(contact.incomingMessages)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(contact.outgoingMessages)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 750 }}>
                        {formatNumber(contact.totalMessages)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(contact.lastActivity)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(report.topContacts ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        Your active conversations will appear here.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Typography
              variant="caption"
              color="text.secondary"
              mr={0.5}
              mt={0.5}
            >
              Connected WhatsApp phone IDs:
            </Typography>
            {(report.phoneNumberIds ?? []).map((phoneNumberId) => (
              <Chip key={phoneNumberId} label={phoneNumberId} size="small" />
            ))}
          </Box>
        </Box>
      ) : null}
    </Container>
  );
};

export default MyConversationReport;
